import { INode as InnerNode, INodeChildren } from '@rchh/lowcode-designer';
import { IPublicTypeNodeData, IPublicEnumTransformStage, IPublicModelNodeChildren, IPublicModelNode } from '@rchh/lowcode-types';
import { Node as ShellNode } from './node';
import { nodeSymbol, nodeChildrenSymbol } from '../symbols';

export class NodeChildren implements IPublicModelNodeChildren {
  private readonly [nodeChildrenSymbol]: INodeChildren;

  constructor(nodeChildren: INodeChildren) {
    this[nodeChildrenSymbol] = nodeChildren;
  }

  static create(nodeChildren: INodeChildren | null): IPublicModelNodeChildren | null {
    if (!nodeChildren) {
      return null;
    }
    return new NodeChildren(nodeChildren);
  }

  /**
   * Return the node that owns this children instance
   */
  get owner(): IPublicModelNode | null {
    return ShellNode.create(this[nodeChildrenSymbol].owner);
  }

  /**
   * Number of node instances in children
   */
  get size(): number {
    return this[nodeChildrenSymbol].size;
  }

  /**
   * @deprecated
   * Whether empty
   * @returns
   */
  get isEmpty(): boolean {
    return this[nodeChildrenSymbol].isEmptyNode;
  }

  /**
   * Whether empty
   * @returns
   */
  get isEmptyNode(): boolean {
    return this[nodeChildrenSymbol].isEmptyNode;
  }

  /**
   * @deprecated
   * judge if it is not empty
   */
  get notEmpty(): boolean {
    return this[nodeChildrenSymbol].notEmptyNode;
  }

  /**
   * judge if it is not empty
   */
  get notEmptyNode(): boolean {
    return this[nodeChildrenSymbol].notEmptyNode;
  }

  /**
   * Remove the given node
   * delete the node
   * @param node
   */
  delete(node: IPublicModelNode): boolean {
    return this[nodeChildrenSymbol].delete((node as any)?.[nodeSymbol]);
  }

  /**
   * Insert a node
   * @param node Node to insert
   * @param at Insert index
   * @returns
   */
  insert(node: IPublicModelNode, at?: number | null): void {
    return this[nodeChildrenSymbol].insert((node as any)?.[nodeSymbol], at);
  }

  /**
   * Return index of the given node
   * @param node
   * @returns
   */
  indexOf(node: IPublicModelNode): number {
    return this[nodeChildrenSymbol].indexOf((node as any)?.[nodeSymbol]);
  }

  /**
   * Array-like splice
   * @param start
   * @param deleteCount
   * @param node
   */
  splice(start: number, deleteCount: number, node?: IPublicModelNode): any {
    this[nodeChildrenSymbol].splice(start, deleteCount, (node as any)?.[nodeSymbol]);
  }

  /**
   * Return node at index
   * @param index
   * @returns
   */
  get(index: number): IPublicModelNode | null {
    return ShellNode.create(this[nodeChildrenSymbol].get(index));
  }

  /**
   * Whether it contains the given node
   * @param node
   * @returns
   */
  has(node: IPublicModelNode): boolean {
    return this[nodeChildrenSymbol].has((node as any)?.[nodeSymbol]);
  }

  /**
   * Array-like forEach
   * @param fn
   */
  forEach(fn: (node: IPublicModelNode, index: number) => void): void {
    this[nodeChildrenSymbol].forEach((item: InnerNode, index: number) => {
      fn(ShellNode.create(item)!, index);
    });
  }

  /**
   * Array-like reverse
   */
  reverse(): IPublicModelNode[] {
    return this[nodeChildrenSymbol].reverse().map(d => {
      return ShellNode.create(d)!;
    });
  }

  /**
   * Array-like map
   * @param fn
   */
  map<T = any>(fn: (node: IPublicModelNode, index: number) => T): T[] | null {
    return this[nodeChildrenSymbol].map<T>((item: InnerNode, index: number): T => {
      return fn(ShellNode.create(item)!, index);
    });
  }

  /**
   * Array-like every
   * @param fn
   */
  every(fn: (node: IPublicModelNode, index: number) => boolean): boolean {
    return this[nodeChildrenSymbol].every((item: InnerNode, index: number) => {
      return fn(ShellNode.create(item)!, index);
    });
  }

  /**
   * Array-like some
   * @param fn
   */
  some(fn: (node: IPublicModelNode, index: number) => boolean): boolean {
    return this[nodeChildrenSymbol].some((item: InnerNode, index: number) => {
      return fn(ShellNode.create(item)!, index);
    });
  }

  /**
   * Array-like filter
   * @param fn
   */
  filter(fn: (node: IPublicModelNode, index: number) => boolean): any {
    return this[nodeChildrenSymbol]
      .filter((item: InnerNode, index: number) => {
        return fn(ShellNode.create(item)!, index);
      })
      .map((item: InnerNode) => ShellNode.create(item)!);
  }

  /**
   * Array-like find
   * @param fn
   */
  find(fn: (node: IPublicModelNode, index: number) => boolean): IPublicModelNode | null {
    return ShellNode.create(
      this[nodeChildrenSymbol].find((item: InnerNode, index: number) => {
        return fn(ShellNode.create(item)!, index);
      }),
    );
  }

  /**
   * Array-like reduce
   * @param fn
   */
  reduce(fn: (acc: any, cur: IPublicModelNode) => any, initialValue: any): void {
    return this[nodeChildrenSymbol].reduce((acc: any, cur: InnerNode) => {
      return fn(acc, ShellNode.create(cur)!);
    }, initialValue);
  }

  /**
   * Import schema
   * @param data
   */
  importSchema(data?: IPublicTypeNodeData | IPublicTypeNodeData[]): void {
    this[nodeChildrenSymbol].import(data);
  }

  /**
   * Export schema
   * @param stage
   * @returns
   */
  exportSchema(stage: IPublicEnumTransformStage = IPublicEnumTransformStage.Render): any {
    return this[nodeChildrenSymbol].export(stage);
  }

  /**
   * Perform add / remove / sort operations
   * @param remover
   * @param adder
   * @param sorter
   */
  mergeChildren(
    remover: (node: IPublicModelNode, idx: number) => boolean,
    adder: (children: IPublicModelNode[]) => any,
    originalSorter: (firstNode: IPublicModelNode, secondNode: IPublicModelNode) => number,
  ) {
    let sorter = originalSorter;
    if (!sorter) {
      sorter = () => 0;
    }
    this[nodeChildrenSymbol].mergeChildren(
      (node: InnerNode, idx: number) => remover(ShellNode.create(node)!, idx),
      (children: InnerNode[]) => adder(children.map((node) => ShellNode.create(node)!)),
      (firstNode: InnerNode, secondNode: InnerNode) => {
        return sorter(ShellNode.create(firstNode)!, ShellNode.create(secondNode)!);
      },
    );
  }
}
